// Ranked candidates with explained reasons, contacts and Assign; excluded teachers collapsed, and assignable anyway
// through the conflict confirmation (SPEC §7.4, §6.1).

import type { Candidate, Candidates, ExcludedTeacher, ScoreReason } from "../../api/types";
import { plural, t, translateCode } from "../../i18n/i18n";
import { appendChildren, createButton, createElement, createExternalLink, createLink } from "../../ui/dom";

export interface CandidatesView {
  candidates: Candidates;
  date: string;
  showExcluded: boolean;
  onToggleExcluded: () => void;
  /** Assigns the teacher as substitute (admins only). */
  onAssign: ((teacherId: string) => Promise<void>) | null;
}

/** Excluded teachers who can't be assigned at all: the lesson's own teacher, and archived teachers. */
const NOT_ASSIGNABLE: string[] = ["CURRENT_TEACHER", "ARCHIVED"];

/** The candidates section; "Nobody is free and qualified" with the excluded list open when empty. */
export function buildCandidatesSection(view: CandidatesView): HTMLElement {
  const section: HTMLElement = createElement("section", "card");
  section.append(createElement("h2", "", t("lesson.candidates")));
  if (view.candidates.candidates.length === 0) {
    section.append(createElement("p", "", t("lesson.nobody")), buildExcludedList(view.candidates.excluded, view));
    return section;
  }

  const wrapper: HTMLDivElement = createElement("div", "table-scroll");
  wrapper.append(buildTable(view));
  section.append(wrapper, buildExcludedToggle(view));
  if (view.showExcluded) {
    section.append(buildExcludedList(view.candidates.excluded, view));
  }

  return section;
}

function buildTable(view: CandidatesView): HTMLTableElement {
  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  const titles: string[] = [t("lesson.rank"), t("app.name"), t("lesson.score"), t("lesson.reasons"),
    t("lesson.contacts")];
  for (const title of titles) {
    head.append(createElement("th", "", title));
  }

  if (view.onAssign !== null) {
    head.append(createElement("th", "", t("app.actions")));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  view.candidates.candidates.forEach((candidate: Candidate, index: number): void => {
    body.append(buildCandidateRow(candidate, index + 1, view));
  });
  return table;
}

function buildCandidateRow(candidate: Candidate, rank: number, view: CandidatesView): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  const name: HTMLAnchorElement = createLink(`/teachers/${candidate.teacherId}?from=${view.date}`,
    `${candidate.lastName} ${candidate.firstName}`);
  appendCells(row, [createElement("span", "", String(rank)), name, createElement("strong", "",
    String(candidate.score)), buildReasonChips(candidate.reasons), buildContacts(candidate)]);
  const onAssign: CandidatesView["onAssign"] = view.onAssign;
  if (onAssign !== null) {
    appendCells(row, [createButton(t("lesson.assign"), (): Promise<void> => onAssign(candidate.teacherId),
      "button-primary")]);
  }

  return row;
}

/** Green chips for points gained, red for points lost, each with text and points (SPEC §7.4). */
export function buildReasonChips(reasons: ScoreReason[]): HTMLElement {
  const chips: HTMLDivElement = createElement("div");
  for (const reason of reasons) {
    const sign: string = reason.points >= 0 ? "+" : "−";
    const text: string = `${sign}${String(Math.abs(reason.points))} ${translateCode("reasons", reason.code,
      reason.params)}`;
    const kind: string = reason.points >= 0 ? "reason-chip-positive" : "reason-chip-negative";
    chips.append(createElement("span", `badge ${kind}`, text));
  }

  return chips;
}

/** Phone as tel:, Telegram as https://t.me/name, email as mailto: (SPEC §7.4). */
export function buildContacts(person: { phone: string | null; email: string | null; telegram: string | null }):
  HTMLElement {
  const contacts: HTMLDivElement = createElement("div", "contacts");
  if (person.phone !== null) {
    contacts.append(createExternalLink("tel:" + person.phone.replace(/[^+\d]/g, ""), person.phone));
  }

  if (person.telegram !== null) {
    const handle: string = person.telegram.replace(/^@/, "");
    contacts.append(createExternalLink(`https://t.me/${encodeURIComponent(handle)}`, "@" + handle));
  }

  if (person.email !== null) {
    contacts.append(createExternalLink("mailto:" + person.email, person.email));
  }

  return contacts;
}

function buildExcludedToggle(view: CandidatesView): HTMLElement {
  const label: string = view.showExcluded
    ? t("lesson.hideExcluded")
    : t("lesson.showExcluded", { count: view.candidates.excludedCount });
  return createButton(label, view.onToggleExcluded, "button-link");
}

function buildExcludedList(excluded: ExcludedTeacher[], view: CandidatesView): HTMLElement {
  const list: HTMLUListElement = createElement("ul", "excluded-list");
  list.setAttribute("aria-label", plural("excluded", excluded.length));
  for (const teacher of excluded) {
    list.append(buildExcludedItem(teacher, view.onAssign));
  }

  return list;
}

/** Name and reason; admins can still assign (the server then asks to confirm the conflicts, SPEC §6.1). */
function buildExcludedItem(teacher: ExcludedTeacher, onAssign: CandidatesView["onAssign"]): HTMLLIElement {
  const reason: string = translateCode("exclusions", teacher.code, teacher.params);
  const item: HTMLLIElement = createElement("li", "", `${teacher.lastName} ${teacher.firstName} — ${reason} `);
  if (onAssign !== null && NOT_ASSIGNABLE.includes(teacher.code) === false) {
    item.append(createButton(t("lesson.assign"), (): Promise<void> => onAssign(teacher.teacherId)));
  }

  return item;
}

function appendCells(row: HTMLTableRowElement, contents: HTMLElement[]): void {
  for (const content of contents) {
    const cell: HTMLTableCellElement = createElement("td");
    appendChildren(cell, content);
    row.append(cell);
  }
}
